import { useCallback, useMemo } from 'react';

import { ANALYSIS_TYPES } from '@core';
import { Activity } from '@data';
import { updateSwingAnalysis, updateSwingImages, updateSwingVideos, upsertSwingToActivity } from '@queries';
import { globalStore } from '@store';

import { dispatch, getMessageHandlers } from '../message/handlers';
import { BeastPayload, BOOTH_MESSAGE } from '../message/schema';
import { isAnalyzingAtom } from '../state/atoms';
import { FullSwing } from '../state/PartialSwingCache';

export type BeastMessageHandlerHookArgs = {
    currentActivity?: Activity | null;
    onActivityIdChange: (activityId: number | null) => void;
};

export const useMessageHandler = ({ currentActivity, onActivityIdChange }: BeastMessageHandlerHookArgs) => {
    const finalizeSwing = useCallback(
        (swing: FullSwing) => {
            if (!currentActivity) return;

            upsertSwingToActivity(currentActivity.id, {
                uuid: swing.uuid,
                activityId: currentActivity.id,
                createdAt: new Date().toISOString(),
                favorite: false,
                name: null,
                clubName: currentActivity?.club?.shortName || '-',
                tags: currentActivity?.tags?.map(({ name }) => name) || [],
            });
            updateSwingAnalysis(currentActivity.userId, swing.uuid, ANALYSIS_TYPES.QUICK_ANALYSIS, swing.analysis);
            updateSwingVideos(swing.uuid, swing.videos ?? []);
            updateSwingImages(swing.uuid, swing.images ?? []);
            globalStore.set(isAnalyzingAtom, false);
        },
        [currentActivity],
    );

    const handlers = useMemo(
        () =>
            currentActivity
                ? getMessageHandlers({ finalizeSwing, onActivityIdChange, userId: currentActivity.userId })
                : undefined,
        [currentActivity, finalizeSwing, onActivityIdChange],
    );

    return useCallback(
        async (payload: BeastPayload): Promise<void> => {
            if (!currentActivity || !handlers) {
                if (payload.type === BOOTH_MESSAGE.BEAST_STATUS) {
                    onActivityIdChange(payload.content.activity_id);
                }
                return;
            }
            await dispatch(handlers, payload.type, payload.content);
        },
        [currentActivity, handlers, onActivityIdChange],
    );
};
